import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import {useLocation, useNavigate, useSearchParams} from 'react-router';
import {getUsersByIds} from '../../../firebase/firestore/users';
import PersonCell, {indexUsersById, userDisplayName} from '../components/PersonCell';
import {listCollectionMaterials} from '../collectionTaskApi';
import openCollectionMaterial from '../viewer/openCollectionMaterial';

export default function ListCollectionMaterials() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const taskFilter = searchParams.get('taskId') || '';
    const [q, setQ] = useState('');
    const [items, setItems] = useState([]);
    const [people, setPeople] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await listCollectionMaterials({
                taskId: taskFilter || undefined,
            });
            setItems(list);
            const ids = [...new Set(list.map((r) => r.createdBy).filter(Boolean))];
            if (ids.length) {
                setPeople(indexUsersById(await getUsersByIds(ids)));
            } else {
                setPeople({});
            }
        } catch (e) {
            setError(e?.message || 'Failed to load materials');
            setItems([]);
            setPeople({});
        } finally {
            setLoading(false);
        }
    }, [taskFilter]);

    useEffect(() => {
        reload();
    }, [reload]);

    const filtered = items.filter((row) => {
        if (!q.trim()) return true;
        const authorName = userDisplayName(people[row.createdBy]) || row.createdBy || '';
        const hay = `${row.cmNumber} ${row.title} ${row.collectionMethod} ${row.taskTitle} ${row.ctNumber} ${authorName}`.toLowerCase();
        return hay.includes(q.trim().toLowerCase());
    });

    return (
        <Box p={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Collected Materials</Typography>
                <Button variant="outlined" onClick={reload}>Refresh</Button>
            </Stack>

            <TextField
                size="small"
                label="Filter"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                sx={{mb: 2, minWidth: 280}}
            />

            {loading && <Stack alignItems="center" py={4}><CircularProgress/></Stack>}
            {error && <Typography color="error">{error}</Typography>}

            {!loading && !error && (
                <Paper variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell sx={{width: '28%'}}>Title</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>Status</TableCell>
                                <TableCell sx={{whiteSpace: 'nowrap'}}>Classification</TableCell>
                                <TableCell>Author</TableCell>
                                <TableCell>Updated</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography color="text.secondary" py={2} align="center">
                                            No materials found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {filtered.map((row) => {
                                const submitted = row.status === 'submitted';
                                return (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        sx={{cursor: 'pointer'}}
                                        onClick={() => openCollectionMaterial({
                                            materialId: row.id,
                                            materialKind: 'cm',
                                            navigate,
                                            location,
                                        })}
                                    >
                                        <TableCell sx={{fontFamily: 'monospace', whiteSpace: 'nowrap'}}>
                                            {row.cmNumber || '—'}
                                        </TableCell>
                                        <TableCell sx={{
                                            maxWidth: 280,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {row.title || '—'}
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap'}}>
                                            {row.collectionMethod || '—'}
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap'}}>
                                            <Chip
                                                size="small"
                                                label={submitted ? 'Submitted' : 'Draft'}
                                                color={submitted ? 'success' : 'default'}
                                                variant={submitted ? 'filled' : 'outlined'}
                                            />
                                        </TableCell>
                                        <TableCell sx={{whiteSpace: 'nowrap'}}>
                                            {row.classification || '—'}
                                        </TableCell>
                                        <TableCell
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{maxWidth: 220}}
                                        >
                                            <PersonCell userId={row.createdBy} profile={people[row.createdBy]}/>
                                        </TableCell>
                                        <TableCell>
                                            {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
}
